import { useCallback, useEffect, useRef, useState } from 'react'

import { BABBLE_TOOLTIP_PORTAL } from 'constants/portals'

interface IUseSimulatorSelectionParams {
  imgElRef: React.RefObject<HTMLImageElement | null>
  isEditor: boolean
  activeState: string | null | undefined
}

const POPUP_SELECTOR =
  '[data-simulator-area-hint], [data-uikit-popup-portal], [data-uikit-popup-overlay], [data-richtext-bubble-toolbar]'

const isClickInsideRichTextPopup = (event: MouseEvent | React.MouseEvent) => {
  const nativeEvent = 'nativeEvent' in event ? (event.nativeEvent as MouseEvent) : event
  const path = (nativeEvent.composedPath?.() ?? []) as EventTarget[]
  for (const node of path) {
    if (!(node instanceof Element)) {
      continue
    }
    if (node.id === BABBLE_TOOLTIP_PORTAL || node.matches?.(POPUP_SELECTOR)) {
      return true
    }
  }
  return false
}

export const useSimulatorSelection = ({
  imgElRef,
  isEditor,
  activeState,
}: IUseSimulatorSelectionParams) => {
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null)
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [showHighlight, setShowHighlight] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSelectArea = useCallback((id: string | null) => {
    setSelectedAreaId(id)
    if (id) {
      setSelectedFieldId(null)
    }
  }, [])

  const handleSelectField = useCallback((id: string | null) => {
    setSelectedFieldId(id)
    if (id) {
      setSelectedAreaId(null)
    }
  }, [])

  const clearHighlight = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setShowHighlight(false)
  }, [])

  const onClickOutside = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        target.closest('[data-simulator-area]') ||
        target.closest('[data-simulator-field]') ||
        isClickInsideRichTextPopup(e)
      ) {
        return
      }

      if (!selectedAreaId && !selectedFieldId && !isEditor) {
        setShowHighlight(true)
      } else {
        clearHighlight()
      }

      setSelectedAreaId(null)
      setSelectedFieldId(null)

      timeoutRef.current = setTimeout(() => {
        setShowHighlight(false)
        timeoutRef.current = null
      }, 1500)
    },
    [selectedAreaId, selectedFieldId, isEditor, clearHighlight],
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isClickInsideRichTextPopup(event)) {
        return
      }
      const target = event.target as Node
      if (imgElRef.current && !imgElRef.current.contains(target)) {
        setSelectedAreaId(null)
        setSelectedFieldId(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgElRef.current])

  useEffect(() => {
    setSelectedAreaId(null)
  }, [activeState])

  return {
    selectedAreaId,
    selectedFieldId,
    showHighlight,
    handleSelectArea,
    handleSelectField,
    clearHighlight,
    onClickOutside,
    setSelectedFieldId,
  }
}
